import { DestroyRef, DOCUMENT, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { fromEvent } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { DeviceService } from './device-detector/device.service';

export const SCROLL_KEY_STATE_FIELD = '__scrollKey';
export const SCROLL_STORAGE_PREFIX = 'scroll:';

/**
 * How long after a restore the document is still allowed to grow into the saved position.
 * A product page returned to from the back button renders its skeleton first and fills in
 * over ~3.5s against a local API, so this is generous on purpose — it costs nothing while
 * nothing is growing, and any real user input ends it early.
 */
const RESTORE_WINDOW_MS = 5000;

/** Input that means the user has taken the viewport back, and a pending restore must stop. */
const USER_INPUT_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

/**
 * The one owner of window scroll across navigations: it restores a remembered position on
 * the way back, and resets to the top on the way to somewhere new.
 *
 * The browser makes exactly one restoration attempt, at the moment the document is
 * whatever height it is then, and clamps to max scroll. That is fine while a page
 * returns at the height it left at — and wrong for a `?page=1-3` list, where the server
 * sends 60 items and the other 120 arrive a few hundred milliseconds later. There is no
 * retry, so the user lands near the top.
 *
 * So restoration moves here: `history.scrollRestoration = 'manual'`, the position is
 * saved per history entry, and a restore keeps watching the content box for the rest of
 * `RESTORE_WINDOW_MS`, re-applying every time the page grows. Watching is what makes this
 * work on a page with nothing server-rendered below the fold: a product page restores onto
 * a skeleton, clamps to near the top, and only reaches the saved position three seconds
 * later. `reapply()` is the same thing on demand, for a page that knows it just grew.
 *
 * Turning the browser's restoration off also takes its reset with it, and the router does
 * not scroll either — `withInMemoryScrolling` is deliberately not configured, since its
 * `RouterScroller` would set `scrollPositionRestoration` itself and race everything here.
 * So the reset is this service's job too; `decideFor()` holds the whole policy.
 *
 * On a cold load the first attempt is made by the inline script at the bottom of
 * `index.html`, which runs before first paint; this service cannot, since it only exists
 * once Angular has bootstrapped. Both read the same key, so the work here is idempotent.
 */
@Service()
export class ScrollRestorationService {
  private document = inject<Document>(DOCUMENT);
  private router = inject(Router);
  private deviceService = inject(DeviceService);
  private destroyRef = inject(DestroyRef);

  private pendingScrollY: number | null = null;
  private hasUserScrolled = false;

  /** The path of the last completed navigation, without query or fragment. */
  private previousPath: string | null = null;

  private restoreFrame: number | null = null;
  private restoreDeadline = 0;
  private lastDocumentHeight = 0;

  init(): void {
    if (!this.deviceService.isPlatformBrowser()) { return; }

    this.destroyRef.onDestroy(() => this.stopWatching());

    history.scrollRestoration = 'manual';

    fromEvent(window, 'scroll', { passive: true })
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // `apply()` scrolls the window, which raises this same event. Counting that as the
        // user taking over switched `reapply()` off ~100ms after every restore — before the
        // backfill this service exists for had a chance to finish — and let a stray scroll
        // overwrite the remembered position on the way.
        if (this.isRestoredPosition()) { return; }

        this.hasUserScrolled = true;
        this.save();
      });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        switch (this.decideFor(event.urlAfterRedirects)) {
          case 'restore':
            this.hasUserScrolled = false;
            this.pendingScrollY = this.read();
            this.apply();
            this.watchWhileContentGrows();
            break;

          case 'top':
            this.stopWatching();
            this.hasUserScrolled = false;
            this.pendingScrollY = 0;
            this.apply();
            // Give the new entry a stored position of its own. Without it, leaving this
            // page and coming back would find nothing saved and leave the viewport
            // wherever the page it returns from was scrolled to.
            this.save(0);
            break;

          case 'keep':
            break;
        }
      });

    this.pendingScrollY = this.read();
    this.apply();
    this.watchWhileContentGrows();
  }

  /**
   * Re-applies the saved position, for a page that has just grown taller. Ignored once
   * the user has scrolled on their own — their position wins over a remembered one.
   */
  reapply(): void {
    if (!this.deviceService.isPlatformBrowser() || this.hasUserScrolled) { return; }
    this.apply();
  }

  /**
   * What a finished navigation should do to the viewport, and the only place that decides
   * it. `keep` covers every navigation that refines the page already on screen rather than
   * replacing it: a fragment the page scrolls to on its own terms, a `replaceUrl` that
   * rewrites the entry in place, and a query-only change — a re-run search, a filter, a
   * page of a list. Everything else is a new destination, and starts at the top.
   */
  private decideFor(urlAfterRedirects: string): 'restore' | 'keep' | 'top' {
    const path = urlAfterRedirects.split(/[?#]/)[0];
    const previousPath = this.previousPath;
    this.previousPath = path;

    // The router sets this on the line above the one that emits NavigationEnd, so it is
    // the navigation that just finished rather than the one before it.
    const navigation = this.router.lastSuccessfulNavigation();

    // Navigation 1 is the router's own first run on this document. Its position was already
    // applied by the inline script in `index.html`, and there is no page it arrived from —
    // so it restores, and never resets. It is also dispatched with `replaceUrl`, which is
    // why it has to be recognised before the refinement branch below.
    if (navigation?.id === 1 || navigation?.trigger === 'popstate') { return 'restore'; }

    if (navigation?.extras.replaceUrl || urlAfterRedirects.includes('#')) { return 'keep'; }

    return path === previousPath ? 'keep' : 'top';
  }

  /**
   * Keeps asking for the saved position while the page is still filling in. A restore onto
   * a page that renders a skeleton first lands wherever the document happens to end — the
   * browser clamps to max scroll — and there is nothing to correct it later, which is the
   * whole reason the position looked lost on the way back to a product.
   *
   * Ends on the first of: the position reached, real user input, or the window running out.
   */
  private watchWhileContentGrows(): void {
    this.stopWatching();
    if (this.pendingScrollY === null || this.isRestoreSatisfied()) { return; }

    this.lastDocumentHeight = this.documentHeight();
    USER_INPUT_EVENTS.forEach(name => window.addEventListener(name, this.onUserInput, { passive: true }));
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.openRestoreWindow();
  }

  /**
   * Starts the budget, but only while someone can see the page. A hidden tab runs no
   * animation frames at all, so the window would expire without a single attempt — a link
   * opened in a background tab, or a tab switched away from while the page was still
   * filling in, would come back sitting at the top. The budget waits for the tab instead.
   */
  private openRestoreWindow(): void {
    if (this.document.visibilityState !== 'visible') { return; }

    this.restoreDeadline = Date.now() + RESTORE_WINDOW_MS;
    this.restoreFrame = requestAnimationFrame(this.step);
  }

  private readonly step = (): void => {
    if (Date.now() > this.restoreDeadline) {
      this.stopWatching();
      this.pendingScrollY = null;
      return;
    }

    const height = this.documentHeight();
    if (height !== this.lastDocumentHeight) {
      this.lastDocumentHeight = height;
      this.apply();
      if (this.isRestoreSatisfied()) {
        this.stopWatching();
        return;
      }
    }

    this.restoreFrame = requestAnimationFrame(this.step);
  };

  private readonly onVisibilityChange = (): void => {
    if (this.document.visibilityState !== 'visible') {
      if (this.restoreFrame !== null) { cancelAnimationFrame(this.restoreFrame); }
      this.restoreFrame = null;
      return;
    }

    if (this.pendingScrollY === null || this.isRestoreSatisfied()) { return; }

    this.apply();
    this.openRestoreWindow();
  };

  /**
   * Bound once so it can be removed again. Wheel, touch and keys are unambiguous — unlike a
   * scroll event, which `apply()` raises itself — so this is the one signal that the user
   * has taken the viewport back, and a half-finished restore must not fight them for it.
   */
  private readonly onUserInput = (): void => {
    this.stopWatching();
    this.pendingScrollY = null;
    this.hasUserScrolled = true;
  };

  private stopWatching(): void {
    if (this.restoreFrame !== null) { cancelAnimationFrame(this.restoreFrame); }
    this.restoreFrame = null;
    USER_INPUT_EVENTS.forEach(name => window.removeEventListener(name, this.onUserInput));
    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /**
   * The height of the document, polled rather than observed. A `ResizeObserver` would be
   * the obvious tool and cannot do this job here: `main.scss` pins `html` and `body` to
   * `height: 100%`, so neither box ever grows with the content, and `app-root` — the one
   * element that does track it — is a custom element left at `display: inline`, which the
   * observer skips entirely. `scrollHeight` is the quantity that actually matters, and
   * reading it assumes nothing about the page's structure or its CSS.
   */
  private documentHeight(): number {
    return this.document.documentElement.scrollHeight;
  }

  private apply(): void {
    if (this.pendingScrollY === null) { return; }
    window.scrollTo({ top: this.pendingScrollY, behavior: 'instant' });
  }

  /** Whether the window has reached the saved position, rather than a clamped stand-in for it. */
  private isRestoreSatisfied(): boolean {
    return this.pendingScrollY !== null && Math.abs(window.scrollY - this.pendingScrollY) < 2;
  }

  /**
   * Whether the window currently sits where `apply()` put it, rather than where a user put it.
   *
   * `apply()` does not always get what it asked for: on a page that has not finished growing
   * the browser clamps to max scroll, and comparing against the raw target then read every
   * one of the service's own scrolls as the user taking over — which switched the restore off
   * and overwrote the saved position with the clamped one, losing it for good.
   */
  private isRestoredPosition(): boolean {
    if (this.pendingScrollY === null) { return false; }

    const maxScrollY = Math.max(0, this.documentHeight() - window.innerHeight);
    return Math.abs(window.scrollY - Math.min(this.pendingScrollY, maxScrollY)) < 2;
  }

  private save(scrollY: number = window.scrollY): void {
    try {
      sessionStorage.setItem(SCROLL_STORAGE_PREFIX + this.entryKey(), String(scrollY));
    } catch {
      // private mode, or storage full — restoration is a convenience, not a requirement
    }
  }

  private read(): number | null {
    try {
      const saved = sessionStorage.getItem(SCROLL_STORAGE_PREFIX + this.entryKey());
      return saved === null ? null : Number(saved);
    } catch {
      return null;
    }
  }

  /**
   * A key for this history entry, stamped into `history.state` so it travels with the
   * entry rather than with the URL — the same URL can appear at several points in the
   * back stack with different positions.
   */
  private entryKey(): string {
    const state = history.state as Record<string, unknown> | null;
    const existing = state?.[SCROLL_KEY_STATE_FIELD];
    if (typeof existing === 'string') { return existing; }

    const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    history.replaceState({ ...(state ?? {}), [SCROLL_KEY_STATE_FIELD]: key }, '', this.document.location.href);
    return key;
  }
}
